/**
 * Temporary Early access: unlock Premium features until v1.0.0 ships.
 * Disable with VITE_EARLY_ACCESS=false before release if needed.
 */

const EARLY_ACCESS_CUTOFF = '1.0.0';
const NOTIFIED_STORAGE_KEY = 'wordsearch-early-access-notified';

function parseSemver(raw: string): [number, number, number] {
  const parts = raw.replace(/^v/i, '').split('.').map((p) => parseInt(p, 10) || 0);
  return [parts[0] ?? 0, parts[1] ?? 0, parts[2] ?? 0];
}

function isVersionBelow(version: string, cutoff: string): boolean {
  const a = parseSemver(version);
  const b = parseSemver(cutoff);
  for (let i = 0; i < 3; i++) {
    if (a[i] < b[i]) return true;
    if (a[i] > b[i]) return false;
  }
  return false;
}

export function getAppVersion(): string {
  const raw = (import.meta.env.VITE_APP_VERSION as string | undefined)?.trim();
  const normalized = raw && raw !== 'undefined' ? raw : '0.0.2';
  return normalized.replace(/^v/i, '');
}

/** True while app version is below 1.0.0 (or VITE_EARLY_ACCESS is explicitly true). */
export function isEarlyAccessActive(): boolean {
  const flag = (import.meta.env.VITE_EARLY_ACCESS as string | undefined)?.trim().toLowerCase();
  if (flag === 'false' || flag === '0') return false;
  if (flag === 'true' || flag === '1') return true;
  return isVersionBelow(getAppVersion(), EARLY_ACCESS_CUTOFF);
}

/** Premium unlocked via Early access (not a paid subscription). */
export function hasEarlyAccessPremium(): boolean {
  return isEarlyAccessActive();
}

export function getEarlyAccessNotifiedVersion(): string | null {
  try {
    return localStorage.getItem(NOTIFIED_STORAGE_KEY);
  } catch {
    return null;
  }
}

export function markEarlyAccessNotified(): void {
  try {
    localStorage.setItem(NOTIFIED_STORAGE_KEY, getAppVersion());
  } catch {
    /* ignore */
  }
}

export function shouldShowEarlyAccessNotice(): boolean {
  if (!isEarlyAccessActive()) return false;
  return getEarlyAccessNotifiedVersion() !== getAppVersion();
}

/** For inline HTML bootstrap (before modules load). */
export function isEarlyAccessActiveForVersion(version: string): boolean {
  return isVersionBelow(version.replace(/^v/i, ''), EARLY_ACCESS_CUTOFF);
}
